import React, { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useSubscription } from '@/context/SubscriptionContext';
import { useConfig } from '@/context/ConfigContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarPlus as CalendarIcon, Pin, PinOff, Pencil, Trash2 } from 'lucide-react';
import { formatDateKey, getDayNameFromDate, getDayNameSpanish, getWeekDays, parseDateKey, getAllWeekdaysOrdered, getWeekdayShortSpanish, getWeekStartDateKey } from "@/lib/dateUtils";
import { cn } from "@/lib/utils";
import { getDogIdentity, dogMatchesIdentity } from "@/lib/dogIdentity";
import { DeleteDogConfirmation } from "@/components/DeleteDogConfirmation";
import { useUser } from "@/context/UserContext";

import { fetchDogDocForDay } from "@/realtimeDogs";
import { resolveNoteForDate } from "@/lib/noteRules";


const normalizeWhatsAppValue = (input) => {
  if (!input?.trim()) return "";

  const clean = input.trim();

  // Conservar intactos los enlaces de WhatsApp ya válidos.
  if (
    clean.startsWith("https://wa.me/") ||
    clean.startsWith("https://chat.whatsapp.com/") ||
    clean.startsWith("https://api.whatsapp.com/")
  ) {
    return clean;
  }

  // Convertir un teléfono con prefijo internacional a una URL de WhatsApp.
  const phoneOnly = clean
    .replace(/[\s\-()]/g, "")
    .replace(/^\+/, "");

  if (/^\d{10,15}$/.test(phoneOnly)) {
    return `https://wa.me/${phoneOnly}`;
  }

  // Si no reconocemos el formato, conservar el contenido introducido.
  return clean;
};


export function EditDogForm({ dog, selectedDayKey, onEdit, onDelete, isOpen, onOpenChange, triggerButton, daysList, currentDate, getInitialDayData, mode = "full", urgentDogs = {}, requireConfirmation = false, onUrgentClick = null, onApplyCanceledDaysBatch = null }) {
   // scheduleOnly = el reloj abre EditDogForm mostrando ÚNICAMENTE la
   // sección Horarios (mismo componente, misma lógica/persistencia, solo
   // cambia qué JSX se renderiza). full = comportamiento actual completo.
   const isScheduleOnly = mode === "scheduleOnly";

   const [editDogName, setEditDogName] = useState("");
   const [editWhatsApp, setEditWhatsApp] = useState("");

   const [editLocationUrl, setEditLocationUrl] = useState("");
   const [editDogCount, setEditDogCount] = useState(1);
   const [editIsFixed, setEditIsFixed] = useState(true);
   const [editSelectedDateKeys, setEditSelectedDateKeys] = useState(new Set());
   const [pickerDate, setPickerDate] = useState(new Date());
   const [canceledPickerDate, setCanceledPickerDate] = useState(new Date());
   const [notesByDay, setNotesByDay] = useState({});
   const [activeNoteDay, setActiveNoteDay] = useState(null);
   const [draftText, setDraftText] = useState("");
   const [onlyThisWeek, setOnlyThisWeek] = useState(false);
   const [draftCanceledDateKeys, setDraftCanceledDateKeys] = useState(new Set());
   const [originalCanceledDateKeys, setOriginalCanceledDateKeys] = useState(new Set());
   const [isCanceledConfirmOpen, setIsCanceledConfirmOpen] = useState(false);
   // Switch INDEPENDIENTE "Solo esta semana" de la sección de Nota
   // informativa. Estado completamente separado de `onlyThisWeek` (que
   // sigue aplicando solo a "Días de actividad"/whatsapp/localización/
   // schedules recurrentes) — nunca deben compartir estado ni handler.
   // Alcance independiente por dateKey: true = puntual, false = recurrente.
   const [notesScopeByDay, setNotesScopeByDay] = useState({});
   const [lastNoteScope, setLastNoteScope] = useState(true);
   // Conjunto de dateKeys cuyo alcance era recurrente (false) y fueron despulsados.
   const [terminatedNoteDays, setTerminatedNoteDays] = useState(new Set());
   const { permissions } = useSubscription();
   const { selectedUser } = useUser();
   const { workspaceId } = useConfig();

   // --- Cancelaciones en semanas futuras: caché + estado de guardado ---
   // weeksCache: { [weekStartDateKey]: { [dateKey]: { dogs, urgentDogs } } }
   // Solo contiene semanas DISTINTAS de la semana principal (esa se lee
   // siempre de daysList/urgentDogs, nunca se cachea aquí para evitar
   // duplicar la fuente de verdad).
   const [weeksCache, setWeeksCache] = useState({});
   // weekStartDateKey de la semana que se está cargando bajo demanda (o null)
   const [loadingWeekKey, setLoadingWeekKey] = useState(null);
   const [saveError, setSaveError] = useState(null);
   const [isSavingCanceledChanges, setIsSavingCanceledChanges] = useState(false);
   // weekStartDateKey de la semana principal (fijado una vez al abrir el
   // editor); nunca se recalcula mientras el diálogo permanece abierto.
   const mainWeekStartKeyRef = useRef(null);
   // weekStartDateKeys cuyo estado inicial de cancelación (originalCanceledDateKeys/
   // draftCanceledDateKeys) ya se sembró desde datos remotos. Evita resembrar
   // (y así pisar ediciones del usuario) si React re-renderiza o el efecto de
   // carga se vuelve a disparar para una semana ya cargada.
   const initializedWeeksRef = useRef(new Set());




   // --- Estados de Horarios ---
   // Array de { id, arrivalTime, departureTime, days: weekday[] }
   const [schedules, setSchedules] = useState([]);
   // null = editor cerrado, 'new' = nuevo, string = id del que se edita
   const [editingScheduleId, setEditingScheduleId] = useState(null);
   // Borrador del horario que se está editando/creando
   // onlyThisWeek: si true, el horario solo se aplica en la semana actual
   const [scheduleDraft, setScheduleDraft] = useState({ arrivalTime: '', departureTime: '', days: [], onlyThisWeek: false });

   // --- Traslado del borrado al editor (sin duplicar lógica) ---
   // Estado de apertura del AlertDialog de confirmación de borrado, reutilizado
   // tal cual (DeleteDogConfirmation.jsx no se modifica).
   const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

   // Ref al contenedor de la sección "Horarios" para poder desplazarnos allí
   // sin alterar su posición/diseño/lógica interna.
   const schedulesSectionRef = useRef(null);

   // dateKeys que YA tenían alguna nota (recurrente resuelta o excepción
   // puntual) al abrir el editor. Se usa exclusivamente para poder
   // distinguir, al vaciar/desmarcar una nota con alcance "solo esta
   // semana", entre "excepción puntual vacía que debe persistir" (el día
   // tenía nota antes) y "nunca hubo nota aquí" (no se debe crear una regla
   // vacía nueva).
   const initialNoteDaysRef = useRef(new Set());

  // Helper: resuelve dogs para un dateKey, probando composite key primero y luego plain key

  const dogsForDayKey = (list, dateKey) => {
    const compositeKey = Object.keys(list).find(k => k.endsWith(`::${dateKey}`));
    return list[compositeKey] ?? list[dateKey] ?? [];
  };

  // --- Semanas futuras de cancelación: helpers de lectura bajo demanda ---

  // Siembra (una sola vez por weekStartKey) los dateKeys ya cancelados
  // (urgentDogs remoto = true) en originalCanceledDateKeys/draftCanceledDateKeys,
  // SOLO para dateKeys donde el perro realmente existe en ese día. No pisa
  // nunca cancelaciones ya presentes de otras semanas (usa unión, no reemplazo).
  const seedCanceledStateForWeek = useCallback((weekStartKey, weekData) => {
    if (initializedWeeksRef.current.has(weekStartKey)) return;
    initializedWeeksRef.current.add(weekStartKey);
    const dogIdentity = getDogIdentity(dog);
    const canceledInWeek = [];
    Object.entries(weekData).forEach(([dateKey, entry]) => {
      const dogs = entry?.dogs || [];
      const remoteUrgent = entry?.urgentDogs || {};
      const exists = dogs.some(d => dogMatchesIdentity(d, dogIdentity));
      if (!exists) return;
      const key = `${dateKey}-${dog.id || dog.name}`;
      if (remoteUrgent[key]) canceledInWeek.push(dateKey);
    });
    if (canceledInWeek.length > 0) {
      setOriginalCanceledDateKeys(prev => new Set([...prev, ...canceledInWeek]));
      setDraftCanceledDateKeys(prev => new Set([...prev, ...canceledInWeek]));
    }
  }, [dog]);

  // Carga bajo demanda de la semana visualizada en el picker de cancelaciones.
  // - Semana principal (mainWeekStartKeyRef): no hace fetch, usa daysList/urgentDogs.
  // - Otra semana: si no está en caché, lee los 7 documentos con fetchDogDocForDay
  //   (nunca crea documentos) y los cachea por weekStartDateKey mientras el
  //   editor esté abierto.
  useEffect(() => {
    if (!isOpen || !dog) return;
    const weekStartKey = getWeekStartDateKey(formatDateKey(canceledPickerDate));
    if (weekStartKey === mainWeekStartKeyRef.current) return;
    if (weeksCache[weekStartKey]) {
      seedCanceledStateForWeek(weekStartKey, weeksCache[weekStartKey]);
      return;
    }

    let cancelled = false;
    setLoadingWeekKey(weekStartKey);

    const weekDayDates = getWeekDays(parseDateKey(weekStartKey));

    Promise.all(
      weekDayDates.map((d) => {
        const dateKey = formatDateKey(d);
        return fetchDogDocForDay(workspaceId, dateKey, selectedUser).then((res) => [dateKey, res]);
      })
    )
      .then((entries) => {
        if (cancelled) return;
        const weekData = {};
        entries.forEach(([dateKey, res]) => {
          weekData[dateKey] = res;
        });
        setWeeksCache((prev) => ({ ...prev, [weekStartKey]: weekData }));
        seedCanceledStateForWeek(weekStartKey, weekData);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[FUTURE_WEEK_LOAD_ERR]', err?.message || err);
      })
      .finally(() => {
        if (!cancelled) setLoadingWeekKey(null);
      });

    return () => { cancelled = true; };
  }, [canceledPickerDate, isOpen, dog, selectedUser, weeksCache, seedCanceledStateForWeek, workspaceId]);

  // Regla exacta de habilitación: un día solo se puede cancelar/descancelar
  // si el perro EXISTE REALMENTE en el documento de ese día (semana principal
  // vía daysList, u otra semana vía caché de fetchDogDocForDay). Nunca se
  // deduce por isFixed/schedules. Si la semana aún no cargó, el día se
  // considera no disponible (deshabilitado) hasta que termine la carga.
  const canCancelDay = useCallback((dateKey) => {
    const weekStartKey = getWeekStartDateKey(dateKey);
    const dogIdentity = getDogIdentity(dog);
    if (weekStartKey === mainWeekStartKeyRef.current) {
      const dayDogs = dogsForDayKey(daysList, dateKey);
      return dayDogs.some(d => dogMatchesIdentity(d, dogIdentity));
    }
    const weekData = weeksCache[weekStartKey];
    if (!weekData || !weekData[dateKey]) return false;
    return (weekData[dateKey].dogs || []).some(d => dogMatchesIdentity(d, dogIdentity));
  }, [dog, daysList, weeksCache]);

  const initializeState = useCallback(() => {

    if (!dog) return;

    setEditDogName(dog.name || "");
    setEditWhatsApp(dog.whatsappGroup || "");
    setEditLocationUrl(dog.locationUrl || "");
    setEditDogCount(dog.count || 1);
    setEditIsFixed(dog.isFixed !== undefined ? dog.isFixed : true);
    setOnlyThisWeek(false);

    const initialPickerDate = parseDateKey(selectedDayKey) || currentDate || new Date();
    setPickerDate(initialPickerDate);
    setCanceledPickerDate(initialPickerDate);

    // Fijar la semana principal (única fuente: daysList/urgentDogs) y limpiar
    // la caché de semanas futuras de una sesión anterior del editor.
    mainWeekStartKeyRef.current = getWeekStartDateKey(formatDateKey(initialPickerDate));
    setWeeksCache({});
    setLoadingWeekKey(null);
    setSaveError(null);
    setIsSavingCanceledChanges(false);


    const weekDaysForPicker = getWeekDays(initialPickerDate);

    const initialPresenceKeys = new Set();
    const initialNotesByDay = {};


    // Fase 2 (corrección): localizar el registro del perro en cada día por
    // IDENTIDAD ESTABLE (dog.id si existe, con fallback legacy a nombre
    // normalizado), reutilizando los helpers de dogIdentity.js. Sin esto,
    // dos clientes distintos con el mismo nombre visible pero id distinto
    // podrían confundirse (mezclar presencia/notas de uno con el otro).
    const dogIdentity = getDogIdentity(dog);

    const initialNotesScopeByDay = {};
    weekDaysForPicker.forEach(d => {
        const dateKey = formatDateKey(d);
        const dayDogData = dogsForDayKey(daysList, dateKey).find(dogInList => dogMatchesIdentity(dogInList, dogIdentity));
        
        if (dayDogData) {

            initialPresenceKeys.add(dateKey);
            
            // Inicializar notas según scope (puntual vs recurrente)
            // A) Si informativeNoteOverride === true: excepción puntual
            if (dayDogData.informativeNoteOverride === true) {
                // Es una excepción puntual: usar dayDogData.informativeNote
                // directamente, incluyendo el caso de excepción puntual VACÍA
                // (informativeNote === "") que representa "sin nota SOLO esta
                // semana" pese a existir una nota recurrente para semanas
                // posteriores. Sin esto, al reabrir se perdía el rastro de la
                // excepción vacía y reaparecía la nota recurrente.
                if (typeof dayDogData.informativeNote === "string") {
                    initialNotesByDay[dateKey] = dayDogData.informativeNote;
                    initialNotesScopeByDay[dateKey] = true;
                }
            } else {
                // B) Si informativeNoteOverride !== true: es una nota recurrente/no protegida
                // Calcular desde recurringNotes, NUNCA usar informativeNote como fallback
                const derivedNote = resolveNoteForDate(
                    Array.isArray(dayDogData.recurringNotes) ? dayDogData.recurringNotes : [],
                    dateKey
                );
                if (derivedNote && derivedNote.trim()) {
                    initialNotesByDay[dateKey] = derivedNote;
                    initialNotesScopeByDay[dateKey] = false;
                }
            }
        }
    });

    // Inicializar cancelaciones desde urgentDogs para la semana actual visible
    const initialCanceledDateKeys = new Set();
    weekDaysForPicker.forEach(d => {
      const dateKey = formatDateKey(d);
      const urgentKey = `${dateKey}-${dog.id || dog.name}`;
      if (urgentDogs[urgentKey]) {
        initialCanceledDateKeys.add(dateKey);
      }
    });

    initialNoteDaysRef.current = new Set(Object.keys(initialNotesByDay));

    setEditSelectedDateKeys(initialPresenceKeys);
    setNotesByDay(initialNotesByDay);
    setNotesScopeByDay(initialNotesScopeByDay);
    setLastNoteScope(
  initialNotesByDay[selectedDayKey] !== undefined
    ? (initialNotesScopeByDay[selectedDayKey] ?? true)
    : true
);
    setTerminatedNoteDays(new Set());
    setDraftText("");
    setActiveNoteDay(
      initialNotesByDay[selectedDayKey] !== undefined ? selectedDayKey : null
    );
    setOriginalCanceledDateKeys(initialCanceledDateKeys);
    setDraftCanceledDateKeys(new Set(initialCanceledDateKeys));

    // Cargar horarios del cliente (deep clone para no mutar el original)
    setSchedules((dog.schedules || []).map(s => ({ ...s, days: [...s.days] })));
    setEditingScheduleId(null);
    setScheduleDraft({ arrivalTime: '', departureTime: '', days: [], onlyThisWeek: false });

  }, [dog, selectedDayKey, currentDate, daysList, urgentDogs]);
  
  useEffect(() => {
    if (isOpen) {
      initializeState();
    }
    // Inicializar SOLO al transicionar isOpen (típicamente al montar, ya que
    // DogListItem monta/desmonta este componente al abrir/cerrar el diálogo).
    // `initializeState` cambia de identidad cada vez que `daysList`/`dog` cambian
    // de referencia (p. ej. en cada snapshot de Firestore, incluso por solo
    // metadata) — si se incluye en las deps, el efecto se re-ejecuta y pisa
    // cualquier edición local del usuario mientras el modal está abierto.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleDateToggle = (dateKey) => {


    setEditSelectedDateKeys((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(dateKey)) {
        newSet.delete(dateKey);
      } else {
        newSet.add(dateKey);
      }
      return newSet;
    });
  };

   const handleNotesDayClick = (dateKey) => {
      if (!canCancelDay(dateKey)) return;
      if (
        activeNoteDay === dateKey ||
        (notesByDay[dateKey] ?? "").trim() !== ""
      ) {
        if (activeNoteDay === dateKey) {
          // Caso C: activo despulsar (quitar nota del borrador)
          const priorScope = notesScopeByDay[dateKey] ?? true;
          setLastNoteScope(priorScope);
          // Si el alcance era puntual ("solo esta semana") y ese día YA tenía
          // una nota (recurrente heredada o excepción previa) antes de abrir
          // el editor, desmarcar la bolita debe dejar una excepción puntual
          // VACÍA explícita (informativeNote:"", informativeNoteOverride:true)
          // en vez de eliminar el registro por completo — de lo contrario
          // computeNoteFieldsForDate no encuentra override puntual y vuelve
          // a heredar la nota recurrente. La recurrencia original no se toca.
          const hadPriorNote = initialNoteDaysRef.current.has(dateKey);
          if (priorScope === true && hadPriorNote) {
            setNotesByDay(prev => ({ ...prev, [dateKey]: "" }));
            setNotesScopeByDay(prev => ({ ...prev, [dateKey]: true }));
            setTerminatedNoteDays(prev => { const s = new Set(prev); s.delete(dateKey); return s; });
          } else {
            setNotesByDay(prev => { const n = {...prev}; delete n[dateKey]; return n; });
            setNotesScopeByDay(prev => { const n = {...prev}; delete n[dateKey]; return n; });
            // Si era recurrente, marcar para terminar esa recurrencia
            if (priorScope === false) {
              setTerminatedNoteDays(prev => new Set(prev).add(dateKey));
            } else {
              // Si era puntual, asegurar que NO queda en terminatedNoteDays
              setTerminatedNoteDays(prev => { const s = new Set(prev); s.delete(dateKey); return s; });
            }
          }
          setActiveNoteDay(null);
        } else {
          // Caso B: con nota pero no activo
          setActiveNoteDay(dateKey);
          setLastNoteScope(notesScopeByDay[dateKey] ?? true);
        }
      } else {
        // Caso A: sin nota
        const textToAssign = activeNoteDay !== null
          ? (notesByDay[activeNoteDay] ?? "")
          : draftText;
        setNotesByDay(prev => ({ ...prev, [dateKey]: textToAssign }));
        // Si el día activo anterior tenía scope, heredarlo
        const inheritedScope = activeNoteDay !== null ? (notesScopeByDay[activeNoteDay] ?? true) : true;
        setLastNoteScope(inheritedScope);
        setNotesScopeByDay(prev => ({ ...prev, [dateKey]: inheritedScope }));
        // Retirar de terminatedNoteDays si estaba marcado
        setTerminatedNoteDays(prev => { const s = new Set(prev); s.delete(dateKey); return s; });
        setActiveNoteDay(dateKey);
      }
    };

  // --- Lógica de Horarios ---

  /**
   * Regla de sustitución silenciosa: cada weekday solo puede estar en un horario.
   * Importante: recurrentes y puntuales son universos independientes.
   * - Un horario onlyThisWeek NO quitará días a horarios recurrentes.
   * - Un horario recurrente NO quitará días a horarios onlyThisWeek.
   * - Entre horarios onlyThisWeek, solo se eliminan días del mismo weekStartDateKey.
   */
  const applyScheduleSubstitution = (currentSchedules, targetId, newDays, targetIsOnlyThisWeek, targetWeekStart) => {
    const updated = currentSchedules.map(s => {
      if (s.id === targetId) {
        return { ...s, days: newDays };
      }
      const sIsPunctual = s.onlyThisWeek ?? false;
      // Solo afectar a horarios del mismo tipo
      if (sIsPunctual !== targetIsOnlyThisWeek) return s;
      // Para horarios puntuales, solo afectar a los de la misma semana
      if (sIsPunctual && s.weekStartDateKey !== targetWeekStart) return s;
      const remaining = s.days.filter(d => !newDays.includes(d));
      return { ...s, days: remaining };
    });
    return updated.filter(s => s.days.length > 0);
  };

  const handleAddScheduleClick = () => {
    setScheduleDraft({ arrivalTime: '', departureTime: '', days: [], onlyThisWeek: false });
    setEditingScheduleId('new');
  };

  const handleEditScheduleClick = (id) => {
    const found = schedules.find(s => s.id === id);
    if (!found) return;
    const isWeekOnly = found.onlyThisWeek ?? false;
    setScheduleDraft({
      arrivalTime: found.arrivalTime || '',
      departureTime: found.departureTime || '',
      days: Array.isArray(found.days) ? [...found.days] : [],
      onlyThisWeek: isWeekOnly,
      // Preservar el weekStartDateKey original del horario.
      // Si era puntual pero falta por legacy, calcular desde la semana actual.
      weekStartDateKey: isWeekOnly
        ? (found.weekStartDateKey || getWeekStartDateKey(selectedDayKey))
        : undefined,
    });
    setEditingScheduleId(id);
  };

  const handleDeleteSchedule = (id) => {
    setSchedules(prev => prev.filter(s => s.id !== id));
    if (editingScheduleId === id) setEditingScheduleId(null);
  };

  const handleScheduleDayToggle = (weekday) => {
    setScheduleDraft(prev => {
      const already = prev.days.includes(weekday);
      return {
        ...prev,
        days: already ? prev.days.filter(d => d !== weekday) : [...prev.days, weekday]
      };
    });
  };

  const handleSaveScheduleDraft = () => {
    if (scheduleDraft.days.length === 0) return;
    const draftIsOnlyThisWeek = scheduleDraft.onlyThisWeek ?? false;
    // Preservar weekStartDateKey del horario original si ya existe.
    // Solo calcular si no hay uno (nuevo horario o legacy sin el campo).
    const draftWeekStart = draftIsOnlyThisWeek
      ? (scheduleDraft.weekStartDateKey || getWeekStartDateKey(selectedDayKey))
      : undefined;

    if (editingScheduleId === 'new') {
      const newSchedule = {
        id: `sched-${Date.now()}`,
        arrivalTime: scheduleDraft.arrivalTime,
        departureTime: scheduleDraft.departureTime,
        days: scheduleDraft.days,
        onlyThisWeek: draftIsOnlyThisWeek,
        ...(draftIsOnlyThisWeek ? { weekStartDateKey: draftWeekStart } : {}),
      };
      setSchedules(prev => {
        const withNew = [...prev, newSchedule];
        return applyScheduleSubstitution(withNew, newSchedule.id, newSchedule.days, draftIsOnlyThisWeek, draftWeekStart);
      });
    } else {
  setSchedules(prev => {
    const original = prev.find(s => s.id === editingScheduleId);
    if (!original) return prev;

    const originalIsOnlyThisWeek = original.onlyThisWeek ?? false;

    // Si un horario recurrente se edita como "Solo esta semana",
    // conservar el recurrente original y crear una excepción puntual.
    if (!originalIsOnlyThisWeek && draftIsOnlyThisWeek) {
      const punctualSchedule = {
        id: `sched-${Date.now()}`,
        arrivalTime: scheduleDraft.arrivalTime,
        departureTime: scheduleDraft.departureTime,
        days: scheduleDraft.days,
        onlyThisWeek: true,
        weekStartDateKey: draftWeekStart,
      };

      const withPunctual = [...prev, punctualSchedule];

      return applyScheduleSubstitution(
        withPunctual,
        punctualSchedule.id,
        punctualSchedule.days,
        true,
        draftWeekStart
      );
    }

    return applyScheduleSubstitution(
      prev,
      editingScheduleId,
      scheduleDraft.days,
      draftIsOnlyThisWeek,
      draftWeekStart
    ).map(s =>
      s.id === editingScheduleId
        ? {
            ...s,
            arrivalTime: scheduleDraft.arrivalTime,
            departureTime: scheduleDraft.departureTime,
            onlyThisWeek: draftIsOnlyThisWeek,
            ...(draftIsOnlyThisWeek
              ? { weekStartDateKey: draftWeekStart }
              : { weekStartDateKey: undefined }),
          }
        : s
    );
  });
}
    setEditingScheduleId(null);
    setScheduleDraft({ arrivalTime: '', departureTime: '', days: [], onlyThisWeek: false });
  };

  const handleCanceledDateToggle = (dateKey) => {
    // Regla exacta de habilitación: usa canCancelDay (semana principal vía
    // daysList, semana futura vía caché de fetchDogDocForDay). Nunca deduce
    // por isFixed/schedules.
    if (!canCancelDay(dateKey)) return;

    setDraftCanceledDateKeys(prev => {

      const newSet = new Set(prev);
      if (newSet.has(dateKey)) {
        newSet.delete(dateKey);
      } else {
        newSet.add(dateKey);
      }
      return newSet;
    });
  };

  const handleResetCanceledDays = () => {
    setDraftCanceledDateKeys(new Set());
  };

  // Aplica cambios de cancelación calculando diffs y delegando al callback batch.
  // NO itera dateKeys ni invoca onUrgentClick aquí — eso lo hace onApplyCanceledDaysBatch.
  // Devuelve una Promise: resuelve tras persistir con éxito (o si no había
  // cambios que aplicar), rechaza si la persistencia remota falla. El
  // llamante decide si cierra el diálogo o muestra error según el resultado.
  const applyCanceledChanges = async (callback) => {
    const originalDateKeys = Array.from(originalCanceledDateKeys);
    const draftDateKeys = Array.from(draftCanceledDateKeys);

    const addedCancellations = draftDateKeys.filter(d => !originalDateKeys.includes(d));
    const removedCancellations = originalDateKeys.filter(d => !draftDateKeys.includes(d));

    // Solo aplicar si hay diferencias reales
    if ((addedCancellations.length > 0 || removedCancellations.length > 0) && onApplyCanceledDaysBatch) {
      // existsByDateKey: confirmación (desde daysList o caché de semana futura)
      // de que el perro está realmente presente en cada dateKey afectado.
      // Se construye aquí porque es la única capa con acceso a ambas fuentes
      // (daysList de la semana principal y weeksCache de semanas futuras).
      const affected = Array.from(new Set([...addedCancellations, ...removedCancellations]));
      const existsByDateKey = {};
      affected.forEach(dk => { existsByDateKey[dk] = canCancelDay(dk); });

      // Delegar al callback batch: esta función aplica TODAS las cancelaciones
      // en UN único setUrgentDogs + UN recordSnapshot, sin pasar por el
      // interceptor de DogList que maneja un solo cliente a la vez.
      // Es async (persiste primero en Firestore); se espera su resultado.
      await onApplyCanceledDaysBatch(
        dog.name,
        dog.id,
        addedCancellations,
        removedCancellations,
        existsByDateKey
      );

    }


    // Ejecutar callback después de aplicar cancelaciones con éxito
    if (callback) callback();
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    const trimmedName = editDogName.trim().toUpperCase();
    if (editSelectedDateKeys.size > 0 && trimmedName) {
      // Verificar si las cancelaciones cambiaron
      const canceledChanged = originalCanceledDateKeys.size !== draftCanceledDateKeys.size ||
        ![...originalCanceledDateKeys].every(d => draftCanceledDateKeys.has(d));
      
      // Si las cancelaciones cambiaron y requireConfirmation es true, mostrar diálogo
      if (canceledChanged && requireConfirmation) {
        setIsCanceledConfirmOpen(true);
        return; // No continuar con onEdit
      }

      // Sin confirmación requerida o sin cambios de cancelación: aplicar directamente
      const count = parseInt(editDogCount, 10);
      const notesByDayFiltered = Object.fromEntries(
        Object.entries(notesByDay).filter(([dateKey, v]) => {
          if (v.trim() !== "") return true;
          // Excepción puntual vacía intencional ("solo esta semana"): el día
          // ya tenía una nota (recurrente heredada o excepción previa) antes
          // de abrir el editor y el usuario la vació (texto borrado o bolita
          // desmarcada) manteniendo el alcance puntual. Debe persistir como
          // informativeNote:"" + informativeNoteOverride:true sin tocar la
          // recurrencia. No se crean reglas vacías nuevas para días que no
          // tenían nota previamente.
          return (notesScopeByDay[dateKey] ?? true) === true && initialNoteDaysRef.current.has(dateKey);
        })
      );

      setSaveError(null);
      setIsSavingCanceledChanges(true);
      try {
        await applyCanceledChanges(() => {
                    onEdit(
            dog.name,
            trimmedName,
            normalizeWhatsAppValue(editWhatsApp),
            isNaN(count) || count < 1 ? 1 : count,
            Array.from(editSelectedDateKeys),
            editIsFixed,
            notesByDayFiltered,
            editLocationUrl.trim(),
            onlyThisWeek,
            schedules,
            notesScopeByDay,
            Array.from(terminatedNoteDays),
            dog.id
          );
        });
        // Cerrar SOLO si la persistencia de cancelaciones (si la había) tuvo éxito.
        onOpenChange(false);
      } catch (err) {
        console.error('[CANCELED_SAVE_ERR]', err?.message || err);
        setSaveError(err?.message || 'save_failed');
        // Mantener el diálogo abierto con el borrador intacto.
      } finally {
        setIsSavingCanceledChanges(false);
      }
    }
  };

  const handleConfirmCanceledChanges = async () => {
    setIsCanceledConfirmOpen(false);

    const count = parseInt(editDogCount, 10);
    const notesByDayFiltered = Object.fromEntries(
      Object.entries(notesByDay).filter(([dateKey, v]) => {
        if (v.trim() !== "") return true;
        // Ver comentario equivalente en handleEditSubmit: conservar la
        // excepción puntual vacía intencional ("solo esta semana").
        return (notesScopeByDay[dateKey] ?? true) === true && initialNoteDaysRef.current.has(dateKey);
      })
    );

    setSaveError(null);
    setIsSavingCanceledChanges(true);
    try {
      // Aplicar cancelaciones y luego continuar con onEdit
      await applyCanceledChanges(() => {
        onEdit(
          dog.name,
          editDogName.trim().toUpperCase(),
          normalizeWhatsAppValue(editWhatsApp),
          isNaN(count) || count < 1 ? 1 : count,
          Array.from(editSelectedDateKeys),
          editIsFixed,
          notesByDayFiltered,
          editLocationUrl.trim(),
          onlyThisWeek,
          schedules,
          notesScopeByDay,
          Array.from(terminatedNoteDays),
          dog.id
        );
      });
      onOpenChange(false);
    } catch (err) {
      console.error('[CANCELED_SAVE_ERR]', err?.message || err);
      setSaveError(err?.message || 'save_failed');
    } finally {
      setIsSavingCanceledChanges(false);
    }
  };


  const weekDaysForPicker = getWeekDays(pickerDate);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogTrigger asChild onClick={(e) => e.stopPropagation()}>
        {triggerButton}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[480px] bg-card max-h-[90vh] flex flex-col" onPointerDownOutside={(e) => e.preventDefault()} onClick={(e) => e.stopPropagation()} onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="text-2xl">
            {isScheduleOnly ? `Horarios de ${dog.name}` : `Editar: ${dog.name}`}
          </DialogTitle>
          {!isScheduleOnly && (
            <DialogDescription className="text-base">
              Configura y edita los datos del cliente.
            </DialogDescription>
          )}
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto pr-2 -mr-2">
          <form id="edit-dog-form" onSubmit={handleEditSubmit} className="space-y-4 py-4">
           {!isScheduleOnly && (
           <>
           <div className="space-y-4 p-4 border rounded-lg bg-card">
             <h3 className="text-lg font-semibold">Datos del cliente</h3>
             <div>
               <Label htmlFor={`editDogName-${dog.name}`}>Nombre del cliente</Label>
               <Input
                 id={`editDogName-${dog.name}`}
                 type="text"
                 value={editDogName}
                 onChange={(e) => setEditDogName(e.target.value)}
                 placeholder="Nombre del cliente/perro"
                 className="mt-1"
                 required
               />
               {editDogName.trim() === "" && (
                 <p className="text-sm text-destructive mt-1">El nombre no puede estar vacío.</p>
               )}
             </div>
             <div>
               <Label htmlFor={`editDogWhatsApp-${dog.name}`}>WhatsApp del cliente</Label>
               <Input
                 id={`editDogWhatsApp-${dog.name}`}
                 type="text"
                 value={editWhatsApp}
                 onChange={(e) => setEditWhatsApp(e.target.value)}
                 placeholder="Número con prefijo o enlace de grupo"
                 className="mt-1"
               />
             </div>
             <div>
               <Label htmlFor={`editDogLocation-${dog.name}`}>Localización de Google Maps</Label>
               <Input
                 id={`editDogLocation-${dog.name}`}
                 type="text"
                 value={editLocationUrl}
                 onChange={(e) => setEditLocationUrl(e.target.value)}
                 placeholder="URL de Google Maps"
                 className="mt-1"
               />
             </div>
             <div>
               <Label htmlFor={`editDogCount-${dog.name}`}>Número de plazas</Label>
               <Input
                 id={`editDogCount-${dog.name}`}
                 type="number"
                 value={editDogCount}
                 onChange={(e) => setEditDogCount(e.target.value)}
                 min="1"
                 className="mt-1"
               />
             </div>
           </div>
          
           <div className="space-y-4 p-4 border rounded-lg bg-card">
             <h3 className="text-lg font-semibold">Nota informativa</h3>
             <div className="flex justify-between items-center">
               <div className="flex items-center gap-2">
                 <Switch
                   id="notesOnlyThisWeek"
                   checked={activeNoteDay !== null ? (notesScopeByDay[activeNoteDay] ?? true) : lastNoteScope}
                    onCheckedChange={(val) => {
                      if (activeNoteDay !== null) {
                        setNotesScopeByDay(prev => ({ ...prev, [activeNoteDay]: val }));
                        setLastNoteScope(val);
                      }
                    }}
                   className="!border-border"
                   disabled={!permissions.canUseInformativeNotes || activeNoteDay === null}
                 />
                 <Label htmlFor="notesOnlyThisWeek" className="text-xs text-muted-foreground cursor-pointer">
                   Solo esta semana
                 </Label>
               </div>
             </div>
             {!permissions.canUseInformativeNotes && (
               <p className="text-xs text-muted-foreground mt-1">Disponible en planes Premium</p>
             )}

             <Input
               id={`informativeNote-${dog.name}`}
               value={activeNoteDay !== null ? (notesByDay[activeNoteDay] ?? "") : draftText}
               onChange={(e) => {
                 if (activeNoteDay !== null) {
                   setNotesByDay(prev => ({ ...prev, [activeNoteDay]: e.target.value }));
                 } else {
                   setDraftText(e.target.value);
                 }
               }}
               placeholder="Escribe una nota y pulsa los días a los que se aplica..."
               className="mt-1"
               disabled={!permissions.canUseInformativeNotes}
             />
             <div className="grid grid-cols-7 gap-1 mt-2">
               {weekDaysForPicker.map((date) => {
                 const dateKey = formatDateKey(date);
                 const dayInitial = getDayNameSpanish(date).charAt(0);
                 const hasActivity = canCancelDay(dateKey);
                 const isSelected =
                    activeNoteDay === dateKey ||
                    (notesByDay[dateKey] ?? "").trim() !== "";
                 const isActive = activeNoteDay === dateKey;
                 return (
                   <Button
                     key={`note-${dateKey}`}
                     type="button"
                     size="icon"
                       className={cn(
                         "h-8 w-8 rounded-full border transition-colors",
                         !hasActivity
                           ? "bg-muted text-muted-foreground border-border cursor-not-allowed opacity-50"
                           : isSelected && isActive
                           ? "bg-primary text-primary-foreground border-primary ring-2 ring-accent ring-offset-1 ring-offset-background"
                           : isSelected
                           ? "bg-primary text-primary-foreground border-primary"
                           : "bg-background text-foreground border-border hover:bg-muted"
                       )}
                     onClick={() => handleNotesDayClick(dateKey)}
                     disabled={!permissions.canUseInformativeNotes || !hasActivity}
                   >
                     {getDayNameSpanish(date).toLowerCase().startsWith('mi') ? 'X' : dayInitial}
                   </Button>
                 );
               })}
             </div>
           </div>
          </>
          )}

          {isScheduleOnly && (
          <>
          {/* --- Sección Horarios --- */}
          <div ref={schedulesSectionRef}>

            <div className="flex items-center gap-2">

              <Label>Horarios</Label>
              {/* Botón añadir (solo si < 7 y no hay editor abierto) */}
              {schedules.length < 7 && editingScheduleId === null && (
                <button
                  type="button"
                  onClick={handleAddScheduleClick}
                  className="mt-2 text-sm text-primary hover:text-primary/80 font-medium transition-colors"
                >
                  Añadir horario
                </button>
              )}
            </div>

            {/* Lista de horarios existentes */}

            {schedules.length > 0 && (
              <div className="space-y-2 mt-2">
                {schedules.map(schedule => (
                  <div key={schedule.id} className="flex items-start justify-between border border-border rounded-lg px-3 py-2 bg-background">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">
                        {schedule.arrivalTime && `Llegada ${schedule.arrivalTime}`}
                        {schedule.arrivalTime && schedule.departureTime && ' · '}
                        {schedule.departureTime && `Salida ${schedule.departureTime}`}
                        {schedule.onlyThisWeek && (
  <span className="ml-2 text-xs font-medium text-amber-600">
    Solo esta semana
  </span>
)}
                        {!schedule.arrivalTime && !schedule.departureTime && (
                          <span className="text-muted-foreground">Sin hora</span>
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {schedule.days.map(d => getWeekdayShortSpanish(d)).join(', ')}
                      </p>
                    </div>
                    <div className="flex gap-1 flex-shrink-0 ml-2">
                      <Button
                        type="button" variant="ghost" size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-foreground"
                        onClick={() => handleEditScheduleClick(schedule.id)}
                        aria-label="Editar horario"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        type="button" variant="ghost" size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-destructive"
                        onClick={() => handleDeleteSchedule(schedule.id)}
                        aria-label="Eliminar horario"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Editor inline de horario */}
            {editingScheduleId !== null && (
              <div className="border border-primary/40 rounded-lg p-3 mt-2 bg-background space-y-3">
                <p className="text-sm font-semibold">
                  {editingScheduleId === 'new' ? 'Nuevo horario' : 'Editar horario'}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Hora de llegada</Label>
                    <Input
                      type="time"
                      value={scheduleDraft.arrivalTime}
                      onChange={e => setScheduleDraft(d => ({ ...d, arrivalTime: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Hora de salida</Label>
                    <Input
                      type="time"
                      value={scheduleDraft.departureTime}
                      onChange={e => setScheduleDraft(d => ({ ...d, departureTime: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Días</Label>
                  <div className="flex flex-wrap gap-2 mt-1.5">
                    {getAllWeekdaysOrdered().map(({ weekday, label }) => {
                      const selected = scheduleDraft.days.includes(weekday);
                      return (
                        <button
                          type="button"
                          key={weekday}
                          onClick={() => handleScheduleDayToggle(weekday)}
                          className={cn(
                            "h-8 w-8 rounded-full border text-xs font-semibold transition-colors select-none",
                            selected
                              ? "bg-primary text-primary-foreground border-primary"
                              : "bg-background text-foreground border-border hover:bg-muted"
                          )}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                  {scheduleDraft.days.length === 0 && (
                    <p className="text-xs text-destructive mt-1">Selecciona al menos un día.</p>
                  )}
                </div>
                {/* Switch "Solo esta semana" para este horario */}
                <div className="flex items-center justify-between py-1">
                  <div className="space-y-0.5">
                    <Label className="text-xs font-medium">Solo esta semana</Label>
                    <p className="text-xs text-muted-foreground">Este horario no se repetirá en semanas futuras.</p>
                  </div>
                  <Switch
                    checked={scheduleDraft.onlyThisWeek ?? false}
                    onCheckedChange={v => setScheduleDraft(d => ({ ...d, onlyThisWeek: v }))}
                  />
                </div>
                <div className="flex gap-2 justify-end">
                  <Button
                    type="button" variant="outline" size="sm"
                    onClick={() => setEditingScheduleId(null)}
                  >
                    Descartar horario
                  </Button>
                  <Button
                    type="button" size="sm"
                    disabled={scheduleDraft.days.length === 0}
                    onClick={handleSaveScheduleDraft}
                  >
                    Añadir horario
                  </Button>
                </div>
              </div>
            )}
          </div>
          </>
          )}

          {!isScheduleOnly && (
          <>
          <div className="space-y-4 p-4 border rounded-lg bg-card">
            <h3 className="text-lg font-semibold">Días de actividad</h3>
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Switch
                  id="onlyThisWeek"
                  checked={onlyThisWeek}
                  onCheckedChange={setOnlyThisWeek}
                  className="!border-border"
                />
                <Label htmlFor="onlyThisWeek" className="text-xs text-muted-foreground cursor-pointer">
                  Solo esta semana
                </Label>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {weekDaysForPicker.map((date) => {
                  const dateKey = formatDateKey(date);
                  const dayName = getDayNameSpanish(date);
                  const isSelected = editSelectedDateKeys.has(dateKey);
                  return (
                    <Button
                      key={dateKey}
                      type="button"
                      onClick={() => handleDateToggle(dateKey)}
                      className={cn(
                        "w-full h-auto py-2 border transition-colors",
                        isSelected
                          ? "bg-primary text-primary-foreground border-primary hover:bg-primary/90"
                          : "bg-card text-foreground border-border hover:bg-muted"
                      )}
                    >
                       <div className="flex flex-col items-center">
                        <span>{dayName}</span>
                        <span className="text-xs opacity-75">{date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}</span>
                      </div>
                    </Button>
                  );
                })}
            </div>
            {editSelectedDateKeys.size === 0 && (
                 <p className="text-sm text-destructive mt-2">Debes seleccionar al menos un día.</p>
            )}
          </div>

          <div className="space-y-4 p-4 border rounded-lg bg-card">
            <h3 className="text-lg font-semibold">Días de cancelación</h3>
            <div className="flex justify-between items-center">
              <div></div>
            </div>
            
            <div className="flex items-center justify-between gap-2 mb-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  const prevDate = new Date(canceledPickerDate);
                  prevDate.setDate(prevDate.getDate() - 7);
                  setCanceledPickerDate(prevDate);
                }}
                className="text-xs"
              >
                ← Anterior
              </Button>
              <span className="text-xs font-medium text-muted-foreground">
                {getWeekDays(canceledPickerDate)[0].toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })} - {getWeekDays(canceledPickerDate)[6].toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  const nextDate = new Date(canceledPickerDate);
                  nextDate.setDate(nextDate.getDate() + 7);
                  setCanceledPickerDate(nextDate);
                }}
                className="text-xs"
              >
                Siguiente →
              </Button>
            </div>

            {(() => {
              const currentWeekStartKey = getWeekStartDateKey(formatDateKey(canceledPickerDate));
              const isLoadingThisWeek = loadingWeekKey === currentWeekStartKey;
              return (
                <>
                  {isLoadingThisWeek && (
                    <p className="text-xs text-muted-foreground mb-2">Cargando semana…</p>
                  )}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2 mb-3">
                    {getWeekDays(canceledPickerDate).map((date) => {
                        const dateKey = formatDateKey(date);
                        const dayName = getDayNameSpanish(date);
                        const dogExists = canCancelDay(dateKey);
                        const isDisabled = !dogExists || isLoadingThisWeek;
                        const isCanceled = draftCanceledDateKeys.has(dateKey);

                        return (
                          <Button
                            key={`canceled-${dateKey}`}
                            type="button"
                            disabled={isDisabled}
                            onClick={() => handleCanceledDateToggle(dateKey)}
                            className={cn(
                              "w-full h-auto py-2 border transition-colors",
                              isDisabled
                                ? "bg-muted text-muted-foreground border-border cursor-not-allowed opacity-50"
                                : isCanceled
                                ? "bg-red-500/30 text-red-700 dark:text-red-400 border-red-500 hover:bg-red-500/40"
                                : "bg-card text-foreground border-border hover:bg-muted"
                            )}
                          >
                             <div className="flex flex-col items-center">
                              <span>{dayName}</span>
                              <span className="text-xs opacity-75">{date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}</span>
                            </div>
                          </Button>
                        );
                      })}
                  </div>
                </>
              );
            })()}

            {saveError && (
              <p className="text-sm text-destructive mb-2">
                No se pudieron guardar los cambios de cancelación. Inténtalo de nuevo.
              </p>
            )}

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleResetCanceledDays}
              disabled={isSavingCanceledChanges}
              className="w-full text-xs"
            >
              Restablecer días cancelados
            </Button>
          </div>

          </>
          )}
        </form>
        </div>

        <DialogFooter className="pt-4 shrink-0 border-t border-border">
           <div className="flex w-full flex-row items-center justify-between">
             <div className="flex items-center gap-2">
               <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="btn-action">
                 {isScheduleOnly ? "Cerrar sin guardar" : "Cancelar"}
               </Button>
               <Button
                 type="submit"
                 form="edit-dog-form"
                 disabled={editSelectedDateKeys.size === 0 || editDogName.trim() === ""}
                 className="btn-action"
               >
                 {isScheduleOnly ? "Guardar cambios" : "Guardar cambios"}
               </Button>
            </div>

            {!isScheduleOnly && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setIsDeleteConfirmOpen(true)}
                aria-label="Eliminar cliente"
                title="Eliminar cliente"
                className="ml-6 h-9 w-9 shrink-0 rounded-md border border-border bg-muted p-0 text-destructive hover:bg-muted/80 hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </DialogFooter>

      </DialogContent>

      {/* Confirmación de borrado trasladada al editor: reutiliza el MISMO
          componente y handler onDelete que antes vivía en la papelera de la
          fila (DeleteDogConfirmation.jsx no se modifica). Mismo scope
          (DELETE_SCOPE.ALL, definido dentro de ese componente), misma
          identidad (dog.id/dog.name) y mismo selectedDayKey.
          Solo se monta en mode="full": scheduleOnly no muestra borrado. */}
      {!isScheduleOnly && (
        <DeleteDogConfirmation
          dogName={dog.name}
          dogId={dog.id}
          selectedDayKey={selectedDayKey}
          onDelete={async (dogName, scope, dayKey, dogId) => {
            // Reenvío exacto al handler existente, sin reimplementar lógica.
            await onDelete?.(dogName, scope, dayKey, dogId);
            // Cerrar el editor SOLO tras un borrado exitoso (si onDelete lanza,
            // DeleteDogConfirmation captura el error y no llega a esta línea).
            onOpenChange(false);
          }}
          isOpen={isDeleteConfirmOpen}
          onOpenChange={setIsDeleteConfirmOpen}
          triggerButton={null}
        />
      )}

      {/* Confirmación de cambios en cancelaciones */}
      <AlertDialog open={isCanceledConfirmOpen} onOpenChange={(open) => {
        if (!open) setIsCanceledConfirmOpen(false);
      }}>
        <AlertDialogContent onClick={(e) => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>Cambios en cancelaciones</AlertDialogTitle>
            <AlertDialogDescription>
              Has modificado los días de cancelación. ¿Quieres guardar estos cambios?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setIsCanceledConfirmOpen(false)}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmCanceledChanges}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Guardar cambios
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}

